import type React from 'react';
import type { TourStep } from '@/components/ui/Tour';
import { TOUR_STEP_IDS } from './tour-constants';

function StepContent({ title, description }: { title: string; description: string }) {
  return (
    <div>
      <h3 className="font-semibold text-foreground mb-1">{title}</h3>
      <p className="text-sm text-muted-foreground">{description}</p>
    </div>
  );
}

export const adminTourSteps: TourStep[] = [
  {
    content: (
      <StepContent
        title="Welcome, Academy Owner!"
        description="This is your admin dashboard. You have full control over your academy platform from here."
      />
    ),
    selectorId: TOUR_STEP_IDS.BUSINESS_OVERVIEW,
    position: 'bottom',
  },
  {
    content: (
      <StepContent
        title="Business at a Glance"
        description="Track your academies, courts, lessons, and revenue potential — all your key numbers in one place."
      />
    ),
    selectorId: TOUR_STEP_IDS.BUSINESS_OVERVIEW,
    position: 'bottom',
  },
  {
    content: (
      <StepContent
        title="Management Tools"
        description="Use these shortcuts to manage your academy, courts, lessons, and coaching staff."
      />
    ),
    selectorId: TOUR_STEP_IDS.MANAGEMENT_TOOLS,
    position: 'top',
  },
  {
    content: (
      <StepContent
        title="Start with Your Organization"
        description="Set up contact details, public site branding, and sports in Organization so students can find you."
      />
    ),
    selectorId: TOUR_STEP_IDS.MANAGE_ORGANIZATION,
    position: 'right',
  },
  {
    content: (
      <StepContent
        title="Set Up Courts"
        description="Add and configure your tennis courts. This is essential before you can create and schedule lessons."
      />
    ),
    selectorId: TOUR_STEP_IDS.MANAGE_COURTS,
    position: 'right',
  },
  {
    content: (
      <StepContent
        title="Create Lessons"
        description="Once courts are set up, create lessons with schedules and prices. Students can then book through your academy page."
      />
    ),
    selectorId: TOUR_STEP_IDS.MANAGE_LESSONS,
    position: 'left',
  },
];

export const studentTourSteps: TourStep[] = [
  {
    content: (
      <StepContent
        title="Your Progress"
        description="See enrolled and completed lessons at a glance on your dashboard."
      />
    ),
    selectorId: TOUR_STEP_IDS.STUDENT_STATS,
    position: 'bottom',
  },
  {
    content: (
      <StepContent
        title="Navigation"
        description="Use the sidebar to move between sections — lessons, calendar, bookings, credits, payments, and student profiles."
      />
    ),
    selectorId: TOUR_STEP_IDS.STUDENT_NAV,
    position: 'right',
  },
  {
    content: (
      <StepContent
        title="Browse Lessons"
        description="Find group lessons, private packages, and camps. Book new sessions for yourself or your children."
      />
    ),
    selectorId: TOUR_STEP_IDS.STUDENT_BROWSE_LESSONS,
    position: 'right',
  },
  {
    content: (
      <StepContent
        title="My Calendar"
        description="View upcoming sessions in a calendar layout with dates, times, and lesson details."
      />
    ),
    selectorId: TOUR_STEP_IDS.STUDENT_CALENDAR,
    position: 'right',
  },
  {
    content: (
      <StepContent
        title="My Bookings"
        description="Manage enrollments — view status, request refunds, rebook after cancellations, and handle waitlists."
      />
    ),
    selectorId: TOUR_STEP_IDS.STUDENT_BOOKINGS,
    position: 'right',
  },
  {
    content: (
      <StepContent
        title="Make-up Credits"
        description="Track credits earned from absences or academy cancellations and redeem them when booking a compatible lesson."
      />
    ),
    selectorId: TOUR_STEP_IDS.STUDENT_CREDITS,
    position: 'right',
  },
  {
    content: (
      <StepContent
        title="Payment History"
        description="Review past lesson payments and receipts when your academy uses online payments."
      />
    ),
    selectorId: TOUR_STEP_IDS.STUDENT_PAYMENTS,
    position: 'right',
  },
  {
    content: (
      <StepContent
        title="My Students"
        description="Manage profiles for your children at this academy — update details and see who each booking is for."
      />
    ),
    selectorId: TOUR_STEP_IDS.STUDENT_MY_STUDENTS,
    position: 'right',
  },
];
